import { state } from '../state.js';
import * as API from '../api.js';

const SOCIAL_VIDEO_POLL_MS = 5000;

const pollState = {
    jobId: '',
    timerId: 0,
    token: 0,
};

function _activeJobId() {
    return String(state.viewerJobId || state.selectedHistoryJobId || state.jobId || '').trim();
}

function _getHistoryJob(jobId) {
    if (!jobId) return null;
    return (state.history || []).find((job) => String(job.job_id || '') === String(jobId)) || null;
}

function _normalizeSocialVideo(job) {
    const social = (job && typeof job.social_video === 'object' && job.social_video) ? job.social_video : {};
    return {
        run_id: String(social.run_id || ''),
        status: String(social.status || '').trim().toLowerCase() || 'missing',
        error_code: String(social.error_code || ''),
        video_url: String(social.video_url || ''),
        poster_url: String(social.poster_url || ''),
    };
}

function _historySocialVideoBadgeState(socialVideo) {
    const status = String((socialVideo && socialVideo.status) || '').trim().toLowerCase();
    if (status === 'complete') return { cls: 'is-ready', text: 'Video ready' };
    if (status === 'queued' || status === 'running') return { cls: 'is-generating', text: 'Video generating' };
    if (status === 'failed') return { cls: 'is-failed', text: 'Video failed' };
    return null;
}

function _syncHistorySocialVideoBadge(jobId) {
    const safeJobId = String(jobId || '').trim();
    if (!safeJobId) return;
    const historyJob = _getHistoryJob(safeJobId)
        || (state.selectedHistoryJob && String(state.selectedHistoryJob.job_id || '') === safeJobId
            ? state.selectedHistoryJob
            : null);
    const socialVideo = _normalizeSocialVideo(historyJob || {});
    const items = Array.from(document.querySelectorAll('.history-item[data-job-id]'));
    const item = items.find((el) => String(el.getAttribute('data-job-id') || '') === safeJobId);
    if (!item) return;
    const tile = item.querySelector('.history-tile');
    if (!tile) return;
    tile.querySelectorAll('.history-social-video-badge').forEach((badge) => badge.remove());
    tile.classList.remove('has-social-video-badge');
    const badgeState = _historySocialVideoBadgeState(socialVideo);
    if (!badgeState) return;
    const badge = document.createElement('div');
    badge.className = `history-social-video-badge ${badgeState.cls}`;
    badge.textContent = badgeState.text;
    tile.classList.add('has-social-video-badge');
    tile.appendChild(badge);
}

function _syncSocialVideoJobState(jobId, socialVideo) {
    const safeJobId = String(jobId || '').trim();
    if (!safeJobId || !socialVideo) return;
    const payload = {
        run_id: String(socialVideo.run_id || ''),
        status: String(socialVideo.status || '').trim().toLowerCase() || 'missing',
        error_code: String(socialVideo.error_code || ''),
        video_url: String(socialVideo.video_url || ''),
        poster_url: String(socialVideo.poster_url || ''),
    };
    const historyJob = _getHistoryJob(safeJobId);
    if (historyJob) {
        historyJob.social_video = payload;
    }
    if (state.selectedHistoryJob && String(state.selectedHistoryJob.job_id || '') === safeJobId) {
        state.selectedHistoryJob.social_video = payload;
    }
    _syncHistorySocialVideoBadge(safeJobId);
}

function _stopSocialVideoPolling() {
    if (pollState.timerId) {
        window.clearTimeout(pollState.timerId);
        pollState.timerId = 0;
    }
    pollState.jobId = '';
    pollState.token += 1;
}

function _renderSocialVideoButton(socialVideo) {
    const btn = document.getElementById('socialVideoBtn');
    if (!btn) return;
    const payload = _normalizeSocialVideo({ social_video: socialVideo });
    btn.classList.add('hidden');
    btn.disabled = true;
    btn.onclick = null;
    btn.removeAttribute('data-state');
    if (!payload.status || payload.status === 'missing') {
        return;
    }
    btn.classList.remove('hidden');
    btn.dataset.state = payload.status;
    if (payload.status === 'complete' && payload.video_url) {
        btn.textContent = 'Open social video';
        btn.disabled = false;
        btn.onclick = () => {
            window.open(payload.video_url, '_blank', 'noopener');
        };
        return;
    }
    if (payload.status === 'failed') {
        btn.textContent = 'Video failed';
        return;
    }
    btn.textContent = 'Video generating';
}

async function _refreshSocialVideoStatus(jobId, options = {}) {
    const safeJobId = String(jobId || '').trim();
    if (!safeJobId) {
        _renderSocialVideoButton({ status: 'missing' });
        return null;
    }
    const token = Number(options.token || pollState.token);
    const response = await API.getSocialVideoStatus(safeJobId);
    if (token !== pollState.token) return response;
    _syncSocialVideoJobState(safeJobId, response || {});
    _renderSocialVideoButton(response || {});
    const status = String((response && response.status) || '').trim().toLowerCase();
    if (status === 'queued' || status === 'running') {
        pollState.jobId = safeJobId;
        pollState.timerId = window.setTimeout(() => {
            _refreshSocialVideoStatus(safeJobId, { token: pollState.token }).catch(() => {});
        }, SOCIAL_VIDEO_POLL_MS);
    }
    return response;
}

export function resetSocialVideoUi() {
    _stopSocialVideoPolling();
    _renderSocialVideoButton({ status: 'missing' });
}

export function refreshActiveSocialVideoUi() {
    _stopSocialVideoPolling();
    _renderSocialVideoButton(_normalizeSocialVideo(_getHistoryJob(_activeJobId()) || {}));
    _refreshSocialVideoStatus(_activeJobId(), { token: pollState.token }).catch(() => {});
}
